import { ArrowDownward, ArrowUpward, Check, Close, Edit } from '@material-ui/icons'
import {
  Box,
  ButtonBase,
  Card,
  CircularProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core'
import { DealAssociationTabs, DealAssociationTabsEnum } from '../components/Tabs'
import { FlexHorizontal, Spacer } from '@vestaboard/installables'
import { MasterPairingSortBy, MasterPairingSortDirection } from '../types'
import React, { useState } from 'react'

import InfiniteScroll from 'react-infinite-scroller'
import { Link as RouterLink } from 'react-router-dom'
import { SearchInput } from '../../../ui/SearchInput'
import { TruncatedId } from '../../truncated-id'
import moment from 'moment'
import { useDrawerContext } from '../../../App'
import { useListMasterPairing } from '../hooks/useListMasterPairings'

export const MasterPairings = () => {
  const [searchTerm, setSearchTerm] = useState<string | null>('')
  const [sortBy, setSortBy] = useState<MasterPairingSortBy | null>(null)
  const [sortDirection, setSortDirection] = useState<MasterPairingSortDirection>(MasterPairingSortDirection.asc)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const { data, fetchMore } = useListMasterPairing({
    input: {
      searchTerm: searchTerm || undefined,
      cursor: null,
      limit: 50,
      sortDirection,
      sortBy
    }
  })
  const { drawerOpen } = useDrawerContext()

  return (
    <>
      <FlexHorizontal spaceBetween>
        <DealAssociationTabs activeTab={DealAssociationTabsEnum.MasterPairings} />
        <FlexHorizontal>
          <SearchInput onSearch={setSearchTerm} value={searchTerm || ''} />
          {searchTerm ? (
            <>
              <Box width={7} />
              <ButtonBase onClick={() => setSearchTerm(null)}>
                <Close />
              </ButtonBase>
            </>
          ) : null}
        </FlexHorizontal>
      </FlexHorizontal>
      <Spacer size='large' />
      <Card
        style={{
          overflow: 'auto',
          width: drawerOpen ? 'calc(100vw - 280px)' : 'calc(100vw - 50px)'
        }}>
        <InfiniteScroll
          pageStart={0}
          loadMore={async () => {
            if (isLoadingMore) {
              return
            }

            setIsLoadingMore(true)
            return await fetchMore({
              updateQuery(previousQueryResult, options) {
                const newRecords = options.fetchMoreResult?.listMasterPairings?.masterPairings || []
                const previousRecords = previousQueryResult.listMasterPairings.masterPairings || []
                const allRecordsDeduped = [...previousRecords, ...newRecords].reduce((acc, current) => {
                  const existing = acc.find(
                    item => item.boardId === current.boardId && item.deviceId === current.deviceId
                  )
                  if (!existing) {
                    acc.push(current)
                  }
                  return acc
                }, [] as typeof newRecords)
                return {
                  listMasterPairings: {
                    nextCursor: options.fetchMoreResult?.listMasterPairings.nextCursor as string,
                    masterPairings: allRecordsDeduped,
                    __typename: previousQueryResult.listMasterPairings.__typename
                  }
                }
              },
              variables: {
                input: {
                  searchTerm: searchTerm || undefined,
                  cursor: data?.listMasterPairings.nextCursor || null,
                  limit: 50,
                  sortDirection,
                  sortBy
                }
              }
            }).then(res => {
              setIsLoadingMore(false)
              return res
            })
          }}
          hasMore={!!data?.listMasterPairings?.nextCursor}
          loader={
            <Box>
              <Spacer size='large' />
              <CircularProgress />
            </Box>
          }>
          <TableContainer>
            <Table>
              <TableHead>
                <TableCell></TableCell>
                <TableCell>Deal ID</TableCell>
                <TableCell>Board ID</TableCell>
                <TableCell>Board Title</TableCell>
                <TableCell>Device ID</TableCell>
                <TableCell>Person ID</TableCell>
                <TableCell>Person Name</TableCell>
                <TableCell>Email Address</TableCell>
                <TableCell>Account ID</TableCell>
                <TableCell style={{ minWidth: 150 }}>Manual Review</TableCell>
                <TableCell style={{ minWidth: 300 }}>Member IDs</TableCell>
                <TableCell style={{ minWidth: 600 }}>Member Emails</TableCell>
                <TableCell style={{ minWidth: 150 }}>
                  <ButtonBase
                    onClick={() => {
                      setSortBy(MasterPairingSortBy.createdAt)
                      setSortDirection(
                        sortDirection === MasterPairingSortDirection.asc
                          ? MasterPairingSortDirection.desc
                          : MasterPairingSortDirection.asc
                      )
                    }}>
                    <>
                      <Typography variant='body2'>Created At</Typography>
                      {sortBy === MasterPairingSortBy.createdAt ? (
                        sortDirection === MasterPairingSortDirection.asc ? (
                          <ArrowDownward />
                        ) : (
                          <ArrowUpward />
                        )
                      ) : null}
                    </>
                  </ButtonBase>
                </TableCell>
                <TableCell style={{ minWidth: 150 }}>
                  <ButtonBase
                    onClick={() => {
                      setSortBy(MasterPairingSortBy.pairingDate)
                      setSortDirection(
                        sortDirection === MasterPairingSortDirection.asc
                          ? MasterPairingSortDirection.desc
                          : MasterPairingSortDirection.asc
                      )
                    }}>
                    <>
                      <Typography variant='body2'>Pairing Date</Typography>
                      {sortBy === MasterPairingSortBy.pairingDate ? (
                        sortDirection === MasterPairingSortDirection.asc ? (
                          <ArrowDownward />
                        ) : (
                          <ArrowUpward />
                        )
                      ) : null}
                    </>
                  </ButtonBase>
                </TableCell>
                <TableCell style={{ minWidth: 200 }}>
                  <ButtonBase
                    onClick={() => {
                      setSortBy(MasterPairingSortBy.plusApplicationDate)
                      setSortDirection(
                        sortDirection === MasterPairingSortDirection.asc
                          ? MasterPairingSortDirection.desc
                          : MasterPairingSortDirection.asc
                      )
                    }}>
                    <>
                      <Typography variant='body2'>Plus Application Date</Typography>
                      {sortBy === MasterPairingSortBy.plusApplicationDate ? (
                        sortDirection === MasterPairingSortDirection.asc ? (
                          <ArrowDownward />
                        ) : (
                          <ArrowUpward />
                        )
                      ) : null}
                    </>
                  </ButtonBase>
                </TableCell>
                <TableCell style={{ minWidth: 200 }}>
                  <ButtonBase
                    onClick={() => {
                      setSortBy(MasterPairingSortBy.plusRenewalDate)
                      setSortDirection(
                        sortDirection === MasterPairingSortDirection.asc
                          ? MasterPairingSortDirection.desc
                          : MasterPairingSortDirection.asc
                      )
                    }}>
                    <>
                      <Typography variant='body2'>Plus Renewal Date</Typography>
                      {sortBy === MasterPairingSortBy.plusRenewalDate ? (
                        sortDirection === MasterPairingSortDirection.asc ? (
                          <ArrowDownward />
                        ) : (
                          <ArrowUpward />
                        )
                      ) : null}
                    </>
                  </ButtonBase>
                </TableCell>
                <TableCell style={{ minWidth: 200 }}>Plus Renewal Frequency</TableCell>
                <TableCell>Synced</TableCell>
              </TableHead>
              <TableBody>
                {data?.listMasterPairings?.masterPairings.map(pairing => (
                  <TableRow key={`${pairing.boardId}:${pairing.deviceId}`}>
                    <TableCell>
                      <RouterLink to={`/deal-associations/board/${pairing.boardId}/device/${pairing.deviceId}`}>
                        <Edit color='action' />
                      </RouterLink>
                    </TableCell>
                    <TableCell>
                      {pairing.dealId ? (
                        <a
                          href={`https://app.hubspot.com/contacts/1984131/record/0-3/${pairing.dealId}`}
                          target='_blank'
                          rel='noopener noreferrer'
                          style={{
                            textDecoration: 'none'
                          }}>
                          <Link>{pairing.dealId}</Link>
                        </a>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <TruncatedId value={pairing.boardId} path={`/boards/${pairing.boardId}`} />
                    </TableCell>
                    <TableCell>{pairing.boardTitle}</TableCell>
                    <TableCell>
                      <TruncatedId value={pairing.deviceId} path={`/devices/${pairing.deviceId}`} />
                    </TableCell>
                    <TableCell>
                      {pairing.personId ? (
                        <TruncatedId value={pairing.personId} path={`/person/${pairing.personId}`} />
                      ) : null}
                    </TableCell>
                    <TableCell>{`${pairing.personFirstName || ''} ${pairing.personLastName || ''}`.trim()}</TableCell>
                    <TableCell>{pairing.accountEmailAddress}</TableCell>
                    <TableCell>{pairing.accountId ? <TruncatedId value={pairing.accountId} /> : null}</TableCell>
                    <TableCell>{pairing.manualReviewFlag ? <Check /> : null}</TableCell>
                    <TableCell>
                      {pairing.memberIds && pairing.memberIds.length > 0
                        ? pairing.memberIds.map(memberId => (
                            <TruncatedId key={memberId} value={memberId} path={`/person/${memberId}`} />
                          ))
                        : null}
                    </TableCell>
                    <TableCell>
                      {pairing.memberEmailAddresses && pairing.memberEmailAddresses.length > 0
                        ? pairing.memberEmailAddresses.join(', ')
                        : null}
                    </TableCell>
                    <TableCell>{pairing.createdAt ? moment(pairing.createdAt).format('MM/DD/YYYY') : null}</TableCell>
                    <TableCell>
                      {pairing.pairingDate ? moment(pairing.pairingDate).format('MM/DD/YYYY') : null}
                    </TableCell>
                    <TableCell>
                      {pairing.plusApplicationDate ? moment(pairing.plusApplicationDate).format('MM/DD/YYYY') : null}
                    </TableCell>
                    <TableCell>
                      {pairing.plusRenewalDate ? moment(pairing.plusRenewalDate).format('MM/DD/YYYY') : null}
                    </TableCell>
                    <TableCell>{pairing.plusRenewalFrequency ? pairing.plusRenewalFrequency : null}</TableCell>
                    <TableCell>{pairing.synced ? <Check /> : null}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </InfiniteScroll>
      </Card>
    </>
  )
}
